<script setup lang="ts">
import { Icon } from '@dashhudson/dashing-ui/icon';
import { computed } from 'vue';

import { useBrandGuard } from '@/composables/useActiveBrand';
import { BRAND } from '@/models/auth/permissions.enum';
import { isAudioMutableMediaType, isMuted } from '@/utils/audioDetection';

const {
  muteSettings = null,
  hasMusic = false,
  mediaType = null,
  dataTest = undefined,
} = defineProps<{
  muteSettings?: { muteAll?: boolean; muteMusic?: boolean } | null;
  hasMusic?: boolean | null;
  mediaType?: string | null;
  dataTest?: string;
}>();

const guard = useBrandGuard();
const hasAudioDetectionPermission = computed(() => guard(BRAND.UGC.CAN_ACCESS_AUDIO_DETECTION));

const muted = computed(() => isMuted(muteSettings));
const showMusic = computed(() => !muted.value && !!hasMusic);
const isVisible = computed(
  () =>
    hasAudioDetectionPermission.value &&
    isAudioMutableMediaType(mediaType) &&
    (muted.value || showMusic.value),
);
const iconName = computed(() => (muted.value ? 'music-muted' : 'music'));
const tooltip = computed(() => {
  if (!muted.value) return 'Music detected';
  return muteSettings?.muteAll ? 'Audio muted' : 'Music muted by Dash';
});
</script>

<template>
  <span
    v-if="isVisible"
    v-dash-tooltip="tooltip"
    class="bg-background-utility flex h-7 w-7 items-center justify-center rounded-full"
    :data-test="dataTest ?? 'MusicMutedBadge'"
  >
    <Icon :name="iconName" size="14" />
  </span>
</template>
